import { useState } from 'react';
import { ArrowUpRight, Link } from 'lucide-react';
import { usePlatform } from '../platform/context';
import { EditableLine } from './EditableText';
import type { Edits } from './useItemEdits';
import type { Item } from '../db/schema';

/**
 * Where this thing came from.
 *
 * Every item gets this, not only imported ones: a chair you had made, or found
 * secondhand, still has a maker's page or a listing worth keeping. It's an
 * editable field like everything else in the panel, so a link can be added to
 * an item that was created from a photo years earlier.
 *
 * Editing it is not the same as importing from it. Typing a link here records
 * where the thing is from; the overflow menu's "Re-import From Link" is what
 * goes and reads the page.
 */
export function SourceLink({ item, edits }: { item: Item; edits: Edits }) {
  const platform = usePlatform();
  const [error, setError] = useState<string | undefined>(undefined);

  const value = edits.value('sourceUrl', item.sourceUrl ?? '');
  const target = openable(value);

  async function open(url: string) {
    setError(undefined);
    try {
      await platform.openExternal(url);
    } catch {
      // The address stays on screen either way, so the page isn't simply
      // unreachable.
      setError("That link couldn't be opened.");
    }
  }

  return (
    <section className="panel__section">
      <h3 className="section-label panel__label">
        <Link size={13} aria-hidden />
        Link
      </h3>

      <div className="panel__link">
        <EditableLine
          className="panel__link-field"
          value={value}
          label="Item link"
          placeholder="Add a link"
          inputMode="url"
          onChange={(next) => edits.set('sourceUrl', next)}
          onCommit={() => edits.commit('sourceUrl')}
        />

        {target !== undefined && (
          <button
            type="button"
            className="panel__open"
            aria-label={`Open ${target.domain}`}
            onClick={() => void open(target.url)}
          >
            <ArrowUpRight size={16} aria-hidden />
          </button>
        )}
      </div>

      {error !== undefined && (
        <p className="panel__warning" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

/**
 * Only offer to open something a browser could actually take. Half-typed text
 * shouldn't grow an open button on every keystroke.
 */
function openable(value: string): { url: string; domain: string } | undefined {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return undefined;
    return { url: parsed.toString(), domain: parsed.hostname.replace(/^www\./, '') };
  } catch {
    return undefined;
  }
}
